import React, { useEffect } from "react"
import styled from "styled-components"
import { BREAKPOINTS } from "../GlobalTheme"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Navigator from "../components/Navigator"
import Icon from "../Icon"

interface MenuCategory {
  name: string,
  options: MenuItem[],
  adminOnly?: boolean
}

interface MenuItem {
  name: string,
  icon: string,
  path: string,
  disabled?: boolean,
  adminOnly?: boolean,
  userOnly?: boolean
}

const CategoryLabel = styled.p<{ $isMenuOpen: boolean }>`
  margin-left: ${props => props.$isMenuOpen ? '3vw' : '1.5vw'};
  font-weight: 700;
  font-size: 12px;
  color: #2D3748;
  padding-top: 10px;
  padding-bottom: 10px;
  white-space: nowrap;
  transition: margin-left .2s ease-in-out;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    margin-left: 16px;
    transition: none;
  }
`

const Option = styled.div<{ $isMenuOpen: boolean, $isSelected: boolean, $isDisbled: boolean }>`
  display: flex;
  width: ${props => props.$isMenuOpen ? '11.5vw' : 'calc(30px + (16px * 2))'};
  margin-left: ${props => props.$isMenuOpen ? '2vw' : '1vw'};
  background-color: ${props => props.$isSelected && '#FFFFFF'};
  border-radius: 15px;
  cursor: ${props => props.$isDisbled ? 'default' : 'pointer'};
  transition: width .2s ease-in-out, margin-left .2s ease-in-out;
  position: relative;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: auto;
    margin-left: 12px;
    margin-right: 12px;
    align-items: center;
    transition: none;
  }
`

const OptionIconContainer = styled.div<{ $isSelected: boolean }>`
  width: 30px;
  height: 30px;
  margin-left: 16px;
  margin-top: 12px;
  margin-bottom: 12px;
  border-radius: 12px;

  ${props => props.$isSelected && `
    background-color: ${props.theme.colors.primary};
  `}
`

const OptionLabel = styled.span<{ $isDisbled: boolean }>`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.$isDisbled ? props.theme.colors.gray_500 : props.theme.colors.dark}/* #2D3748*/;
  line-height: calc(30px + (12px * 2));
  margin-left: 16px;
  white-space: nowrap;
`

const SoonBadge = styled.p`
  /* Anchored to the option's right edge. At 1280px the option is 11.5vw = 147px, which the longest
     labels already fill, so the badge clips their last characters. Putting it in flow instead is
     worse: the label then wraps onto its own line and separates from its icon, so this stays as it
     is and the sidebar reads correctly at the widths the dashboard is actually designed for. */
  position: absolute;
  right: 10px;
  font-weight: 600;
  font-size: 10px;
  color: ${props => props.theme.colors.white};
  background: linear-gradient(140.77deg, #F748A6 17.7%, #DC1A48 79.78%);
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 4px;
  margin: 0px;
  margin-top: 18px;
`

export default function Menu({ isMenuOpen, selectedMenuItem, setSelectedMenuItem, isAdmin }: { isMenuOpen: boolean, selectedMenuItem: string, setSelectedMenuItem: (value: string) => void, isAdmin: boolean }) {
  const { t } = useTranslation()
  const location = useLocation()

  const menuItems: MenuCategory[] = [
    {
      name: '',
      options: [
        {
          name: 'dashboard',
          icon: 'menu.dashboard',
          path: '/',
          disabled: true
        },
        {
          name: 'chargers',
          icon: 'menu.chargers',
          path: '/chargers'
        },
        {
          name: 'rfids',
          icon: 'menu.rfids',
          path: '/rfids',
          userOnly: true
        },
        {
          name: 'chargerGroups',
          icon: 'menu.chargergroups',
          path: '/chargergroups',
          adminOnly: true,
        },
        {
          name: 'errorLogs',
          icon: 'menu.logs',
          path: '/errorlogs',
          adminOnly: true,
        }
      ]
    },
    {
      name: 'admin',
      adminOnly: true,
      options: [
        {
          name: 'appVersion',
          icon: 'menu.appversion',
          path: '/appversion'
        },
        {
          name: 'users',
          icon: 'menu.users',
          path: '/users'
        },
        {
          name: 'settings',
          icon: 'menu.settings',
          path: '/settings',
          disabled: true
        },
        {
          name: 'notifications',
          icon: 'menu.notifications',
          path: '/notifications',
          disabled: true
        }
      ]
    }
  ]

  useEffect(() => {
    setCurrentMenuItem()
  }, [location.pathname])

  function setCurrentMenuItem() {
    const pathName = `/${location.pathname.split('/')[1]}`
    for (const menuItem of menuItems) {
      for (const option of menuItem.options) {
        if (option.path == pathName) {
          setSelectedMenuItem(option.name)
        }
      }
    }
  }

  return (
    <>
      {menuItems && menuItems.map((category) => (category.adminOnly ? isAdmin : true) && (
        <React.Fragment key={`${category.name}`}>
          {category.name && (
            <CategoryLabel $isMenuOpen={isMenuOpen}>{t(`menu_${category.name}`)}</CategoryLabel>
          )}
          {category.options.map(item => (item.adminOnly ? isAdmin : item.userOnly ? !isAdmin : true) && (
            <Navigator to={!item.disabled ? item.path : ''} key={`${category.name}-${item.name}`}>
              <Option onClick={() => !item.disabled && setCurrentMenuItem} $isMenuOpen={isMenuOpen} $isSelected={item.name == selectedMenuItem} $isDisbled={item.disabled || false}>
                <OptionIconContainer $isSelected={item.name == selectedMenuItem}>
                  <Icon path={item.name == selectedMenuItem ? item.icon + "Active" : item.icon} width="16px" height="16px" marginleft="7px" margintop="7px" color="none" />
                </OptionIconContainer>
                {isMenuOpen && (
                  <>
                    {item.disabled ? (
                      <>
                        <OptionLabel $isDisbled={item.disabled || false}>{t(`menu_${item.name}`)}</OptionLabel>
                        <SoonBadge>Soon</SoonBadge>
                      </>
                    ) : (
                      <OptionLabel $isDisbled={item.disabled || false}>{t(`menu_${item.name}`)}</OptionLabel>
                    )}
                  </>
                )}
              </Option>
            </Navigator>
          ))}
        </React.Fragment>
      ))}
    </>
  )
}